/**
 * SHAH - Smart Hub for AI & Humans
 * Premium dashboard landing page + interactive chatbot console
 */

import React, { useState, useEffect, useRef } from 'react';
import ShahService from './services/ShahService';
import './styles/ShahApp.css';

const ShahApp = () => {
  const [messages, setMessages] = useState([
    {
      sender: 'assistant',
      text: 'Hello! I am the SHAH Web Assistant. I communicate with the local SHAH Gateway using a secure DynamoDB job queue. Ask me anything about my capabilities, supported domains, or integration channels!',
      timestamp: new Date().toISOString()
    }
  ]);
  const [inputText, setInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [workerActive, setWorkerActive] = useState(false);
  const [lastCheck, setLastCheck] = useState(null);
  
  const chatEndRef = useRef(null);
  const sessionIdRef = useRef(null);

  // Initialize Session ID
  useEffect(() => {
    let sid = localStorage.getItem('shah_session_id');
    if (!sid) {
      sid = 'web-' + Math.random().toString(36).substring(2, 15);
      localStorage.setItem('shah_session_id', sid);
    }
    sessionIdRef.current = sid;

    // Load worker health immediately
    checkHealth();

    // Set up interval to poll worker health every 30s
    const healthInterval = setInterval(checkHealth, 30000);
    return () => clearInterval(healthInterval);
  }, []);

  // Scroll to bottom of chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const checkHealth = async () => {
    try {
      const status = await ShahService.getHealth();
      setWorkerActive(status.worker_active);
      setLastCheck(new Date());
    } catch (err) {
      console.warn('Failed to fetch worker health:', err);
    }
  };

  const handleSend = async (e) => {
    if (e) e.preventDefault();
    const text = inputText.trim();
    if (!text || isTyping) return;

    // Add user message to UI
    const userMsg = {
      sender: 'user',
      text,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMsg]);
    setInputText('');
    setIsTyping(true);

    try {
      // Send message to Lambda queue
      const result = await ShahService.sendChatMessage(
        text,
        sessionIdRef.current,
        {
          page_url: window.location.href,
          user_agent: navigator.userAgent
        }
      );

      if (result.ok && result.job_id) {
        // Start polling for the completion of the job
        pollForResponse(result.job_id);
      } else {
        throw new Error('No job ID returned');
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      setMessages(prev => [
        ...prev,
        {
          sender: 'assistant',
          text: 'Sorry, I failed to send your message to the serverless queue. Please try again.',
          timestamp: new Date().toISOString(),
          isError: true
        }
      ]);
      setIsTyping(false);
    }
  };

  const pollForResponse = async (jobId, attempt = 1, maxAttempts = 30) => {
    if (attempt > maxAttempts) {
      setMessages(prev => [
        ...prev,
        {
          sender: 'assistant',
          text: 'The request timed out. The local SHAH Gateway might be offline or busy. Please try again later.',
          timestamp: new Date().toISOString(),
          isError: true
        }
      ]);
      setIsTyping(false);
      return;
    }

    try {
      const status = await ShahService.pollChatResponse(sessionIdRef.current, jobId);
      if (status.status === 'completed') {
        setMessages(prev => [
          ...prev,
          {
            sender: 'assistant',
            text: status.response,
            timestamp: new Date().toISOString()
          }
        ]);
        setIsTyping(false);
      } else if (status.status === 'failed') {
        setMessages(prev => [
          ...prev,
          {
            sender: 'assistant',
            text: 'I encountered an error during execution: ' + (status.error || 'Failed processing'),
            timestamp: new Date().toISOString(),
            isError: true
          }
        ]);
        setIsTyping(false);
      } else {
        // Still pending or in_progress, poll again after 1.5 seconds
        setTimeout(() => pollForResponse(jobId, attempt + 1), 1500);
      }
    } catch (err) {
      console.warn('Polling error:', err);
      // Wait and retry
      setTimeout(() => pollForResponse(jobId, attempt + 1), 1500);
    }
  };

  return (
    <div className="shah-dashboard">
      <div className="shah-header-banner">
        <div className="banner-logo">🎯</div>
        <div className="banner-title-area">
          <h1>SHAH — Smart Hub for AI & Humans</h1>
          <p>Multi-channel AI orchestration platform spanning 75+ domains across 18 industries</p>
        </div>
        <div className={`worker-status-badge ${workerActive ? 'online' : 'offline'}`}>
          <span className="status-dot"></span>
          {workerActive ? 'SHAH Gateway: ONLINE' : 'SHAH Gateway: OFFLINE'}
        </div>
      </div>

      <div className="dashboard-layout">
        {/* Left Column: Info & Details */}
        <div className="info-column">
          {/* Key Metrics */}
          <div className="metrics-grid">
            <div className="metric-tile">
              <span className="metric-num">75+</span>
              <span className="metric-lbl">Engineering Domains</span>
            </div>
            <div className="metric-tile">
              <span className="metric-num">18</span>
              <span className="metric-lbl">Industries</span>
            </div>
            <div className="metric-tile">
              <span className="metric-num">3,000+</span>
              <span className="metric-lbl">Test Cases</span>
            </div>
            <div className="metric-tile">
              <span className="metric-num">31</span>
              <span className="metric-lbl">Active AI Tools</span>
            </div>
          </div>

          {/* Capabilities */}
          <div className="dashboard-section-card">
            <h3>🛡️ Platform Capabilities</h3>
            <div className="capabilities-list">
              <div className="capability-item">
                <h4>75+ Engineering Domains</h4>
                <p>Mechanical (FEA, CFD), Electrical (VHDL, SPICE), Chemical (Kinetics), Aerospace (Orbital Mechanics), Data, Devops, and web systems.</p>
              </div>
              <div className="capability-item">
                <h4>9 Communication Channels</h4>
                <p>One unified gateway managing integrations through WhatsApp, Slack, Telegram, Discord, Signal, Gmail, Outlook, LinkedIn, and Web.</p>
              </div>
              <div className="capability-item">
                <h4>23 Programming Languages</h4>
                <p>Fully automated isolated Docker sandboxes and L2 validation runs across languages ranging from Python, Rust, Go, to COBOL.</p>
              </div>
              <div className="capability-item">
                <h4>Self-Improving Test Loop</h4>
                <p>The runner triggers tests, diagnoses compiler/runtime failures with AI, drafts code repairs, and re-tests autonomously.</p>
              </div>
            </div>
          </div>

          {/* Architecture flow */}
          <div className="dashboard-section-card">
            <h3>⚙️ Pipeline Architecture</h3>
            <div className="architecture-steps">
              <div className="step-row">
                <span className="step-badge">1</span>
                <div>
                  <strong>Receive & Classify</strong>
                  <p>Accepts input from any of the 9 channels and determines the necessary actions.</p>
                </div>
              </div>
              <div className="step-row">
                <span className="step-badge">2</span>
                <div>
                  <strong>Orchestrate</strong>
                  <p>Selects optimal LLM engine and models, assigning sub-agents with specialized toolsets.</p>
                </div>
              </div>
              <div className="step-row">
                <span className="step-badge">3</span>
                <div>
                  <strong>Validate & Deliver</strong>
                  <p>Runs output code in a secure sandbox, parses test results, and sends payload to destination.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Interactive Chat Console */}
        <div className="chat-column">
          <div className="chat-console-card">
            <div className="console-header">
              <div className="header-title">
                <span className="terminal-dot red"></span>
                <span className="terminal-dot yellow"></span>
                <span className="terminal-dot green"></span>
                <span className="console-label">SHAH Agent Chat Console</span>
              </div>
              {lastCheck && (
                <span className="console-sync-time">
                  Sync: {lastCheck.toLocaleTimeString()}
                </span>
              )}
            </div>

            <div className="console-body">
              <div className="chat-history">
                {messages.map((msg, index) => (
                  <div key={index} className={`chat-bubble-container ${msg.sender}`}>
                    <div className={`chat-bubble ${msg.sender} ${msg.isError ? 'error' : ''}`}>
                      <div className="bubble-text">{msg.text}</div>
                      <div className="bubble-time">
                        {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                ))}
                
                {isTyping && (
                  <div className="chat-bubble-container assistant">
                    <div className="chat-bubble assistant typing">
                      <span className="dot"></span>
                      <span className="dot"></span>
                      <span className="dot"></span>
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>

              <form className="console-input-form" onSubmit={handleSend}>
                <input
                  type="text"
                  className="console-input-field"
                  placeholder={isTyping ? "Awaiting SHAH Gateway response..." : "Ask SHAH something..."}
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  disabled={isTyping}
                />
                <button
                  type="submit"
                  className="console-send-btn"
                  disabled={!inputText.trim() || isTyping}
                >
                  Send
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShahApp;
